import React, { createContext, useContext, useState, ReactNode } from 'react';

export type ModalType = 
  | 'sensorCalibrations'
  | 'calibrationPreview'
  | 'calibrationResult'
  | 'calibrationModal'
  | 'addSensor'
  | 'bulkUpload'
  | 'startCalibration'
  | 'bulkTemplate'
  | 'individualInput'
  | null;

interface ModalContextType {
  activeModal: ModalType;
  openModal: (modalType: ModalType) => void;
  closeModal: () => void;
  closeAllModals: () => void;
  isModalOpen: (modalType: ModalType) => boolean;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  const openModal = (modalType: ModalType) => {
    // Automatically close any existing modal when opening a new one
    setActiveModal(modalType);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const closeAllModals = () => {
    setActiveModal(null);
  };

  const isModalOpen = (modalType: ModalType) => {
    return activeModal === modalType;
  };

  const value: ModalContextType = {
    activeModal,
    openModal,
    closeModal,
    closeAllModals,
    isModalOpen,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
